import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import {
  IRoamingInternationalCountry,
  IZone,
} from "@nju/data-access-portal-dashboard";
import { Combobox, ErrorMessage } from "@nju/form";
import { portalRoutes } from "@nju/routes";
import {
  A,
  Button,
  ButtonsWrapper,
  CircleLoader,
  GeneralErrorNotification,
  Loader,
  Separator,
  Text,
} from "@nju/ui";
import { chooseCountryMachine } from "./chooseCountry.machine";

export function ChooseCountryPage() {
  const router = useRouter();
  const { userId } =
    portalRoutes.mojeKonto.uslugiDodatkowe.rozmowyMiedzynarodowe.index
      .input(router.query)
      .unwrap();

  const [current, send] = useMachine(chooseCountryMachine, {
    actions: {
      onSubmitSuccess: (_, event) => {
        if (event.type === "SUBMIT" && selectedCountry) {
          router.push(
            portalRoutes.mojeKonto.uslugiDodatkowe.rozmowyMiedzynarodowe.podsumowanie.get(
              {
                ...(userId && { userId }),
                country: selectedCountry.displayName.pl,
                zone: selectedCountry.zones?.find((zone) => {
                  return !zone.isPromo;
                })?.name as IZone["name"],
                zoneName: selectedCountry.zones?.find((zone) => {
                  return !zone.isPromo;
                })?.displayName?.pl,
              },
            ),
          );
        }
      },
    },
  });

  const { context, matches } = current;
  const { filteredCountries, selectedCountry, formError, countrySearch } =
    context;

  const handleSelectCountry = React.useCallback(
    (selectedCountries: IRoamingInternationalCountry[]) => {
      send({ type: "SELECT_COUNTRY", selectedCountry: selectedCountries[0] });
    },
    [send],
  );

  const handleSetCountrySearch = React.useCallback(
    (search: string) => {
      send({ type: "SET_COUNTRY_SEARCH", search });
    },
    [send],
  );

  const handleSubmit = React.useCallback(() => {
    send({ type: "SUBMIT" });
  }, [send]);

  if (matches("error")) {
    return (
      <GeneralErrorNotification
        callback={() => {
          return router.reload();
        }}
      />
    );
  }

  if (matches("loading")) {
    return (
      <div className="flex justify-center items-center h-full">
        <CircleLoader />
      </div>
    );
  }

  return (
    <div className="flex gap-12 flex-col from-desktop:flex-row max-w-[715px] mx-auto">
      <div>
        <Text
          size={{ "@onlyMobile": "keyS", "@fromTablet": "keyM" }}
          weight="bold"
        >
          pakiety rozmów międzynarodowych
        </Text>
        <Separator size="s" tone="invisible" />
        <Text
          size={{
            "@fromTablet": "l",
            "@onlyMobile": "m",
          }}
        >
          Wybierz z listy kraj, do którego chcesz dzwonić z Polski.
        </Text>
        <Separator size="xs" tone="invisible" />
        <Link
          href={portalRoutes.mojeKonto.uslugiDodatkowe.rozmowyMiedzynarodowe.strefy.get(
            {
              ...(userId && { userId }),
            },
          )}
          passHref={true}
        >
          <A display="inline" size="inline" variant="neutral">
            <Text withUnderline={true}>zobacz strefy międzynarodowe</Text>
          </A>
        </Link>
        <Separator
          size={{
            "@onlyMobile": "s",
            "@fromTablet": "xs",
          }}
          tone="invisible"
        />
        <Combobox
          inputDisplayValue={([value]) => {
            return value?.displayName.pl || "";
          }}
          itemComparator={(first, second) => {
            return first.displayName.pl === second.displayName.pl;
          }}
          items={countrySearch?.length >= 3 ? filteredCountries : []}
          label="kraj"
          listColorVariant="gray"
          listItemRenderer={({ item }) => {
            return (
              <div className="flex items-center gap-2">
                {item.flagImage?.url && (
                  <img
                    alt={item.displayName.pl}
                    className="w-[24px] h-[24px] rounded-full"
                    src={item.flagImage.url}
                  />
                )}
                {!item.flagImage?.url && (
                  <img
                    alt={item.displayName.pl}
                    className="w-[24px] h-[24px] rounded-full"
                    src="/images/not_found.png"
                  />
                )}
                <div key={item.displayName.pl}>
                  <Text weight="bold">{item.displayName.pl}</Text>
                  <Separator size="none" tone="invisible" />
                  {
                    item.zones.find((zone) => {
                      return !zone.isPromo;
                    })?.displayName.pl
                  }
                </div>
              </div>
            );
          }}
          onInputChange={handleSetCountrySearch}
          onSelect={handleSelectCountry}
          state={formError ? "error" : undefined}
          suffix={matches("loading") ? <Loader /> : undefined}
          testId="international-choose-country-input"
        />
        {formError && (
          <>
            <Separator size="xxs" tone="invisible" />
            <ErrorMessage message={formError} />
          </>
        )}
        <ButtonsWrapper>
          <Link
            href={portalRoutes.mojeKonto.index.get({ mode: "mobile" })}
            passHref={true}
          >
            <A size={{ "@onlyMobile": "s" }} variant="secondary">
              anuluj
            </A>
          </Link>
          <Button
            isLoading={matches("success")}
            onPress={handleSubmit}
            size={{ "@onlyMobile": "s" }}
          >
            dalej
          </Button>
        </ButtonsWrapper>
      </div>
    </div>
  );
}
